import * as Sentry from "@sentry/react"
import React from "react"
import { useDispatch, useSelector } from "react-redux"

import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material"
import { grey } from "@mui/material/colors"
import { BiCheckCircle, BiDownload, BiPlusCircle, BiTrash, BiUpload } from "react-icons/bi"

import { MasterRecordStage } from "@/codegen/graphql-request"
import { displayRecords, masterRecordsSlice } from "@/slice/masterRecordsSlice"
import { useColumnTypes, useReferenceConfigs, useTableSchema } from "@/slice/masterSchemaSlice"
import { uiSlice } from "@/slice/uiSlice"
import { RootState } from "@/store"
import { base64ToBlob, download } from "@/util/download"
import { gqlClient } from "@/util/graphql"

import { Cell } from "./Cell"
import { ColumnConfigButton } from "./ColumnConfigButton"
import { ColumnFunction } from "./ColumnFunction"
import { ColumnSearch } from "./ColumnSearch"
import { ColumnSettingModal } from "./ColumnSettingModal"
import { DeleteTableDataConfirmationModal } from "./DeleteTableDataConfirmationModal"
import { DnDHeader } from "./DnDHeader"
import { DuplicatedPrimaryKeysConfirmationModal } from "./DuplicatedPrimaryKeysConfirmationModal"
import { FuncButton } from "./FuncButton"
import { FuncMiniButton } from "./FuncMiniButton"
import { Resizer } from "./Resizer"
import { TablePagination } from "./TablePagination"
import { TablePicker } from "./TablePicker"
import { TemporaryAlert } from "./TemporaryAlert"
import { useResizer } from "./useResizer"

const headerBorderColor = grey[400]
const cellBorderColor = grey[400]
const temporaryCellColor = grey[100]
const headerTextColor = grey[700]
const headerTextColor2 = grey[500]
const tableLeftSpace = 72

export const Table = (props: { tableName: string }) => {
	const { tableName } = props

	const dispatch = useDispatch()

	const allColumnTypes = useColumnTypes()
	const columnTypes = allColumnTypes[tableName]
	const tableSchema = useTableSchema(tableName)
	const tableData = useSelector((state: RootState) => state.masterRecords.data[tableName ?? ""])
	const primaryKeyName = tableSchema?.primaryKey

	const [isFetchingMasterData, setIsFetchingMasterData] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (!tableData) {
			setIsFetchingMasterData(true)
		}
		gqlClient()
			.masterRecords({ tableName })
			.then((res) => {
				setIsFetchingMasterData(false)
				dispatch(
					masterRecordsSlice.actions.setData({ tableName, data: res.masterRecords, columnTypes, additional: false }),
				)
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, tableName])

	// store

	const columnNames = useSelector((state: RootState) => {
		const schema = state.masterSchema.data.find((data) => data.tableName == tableName)
		if (!schema) return []
		return schema.columns.map((c) => ({
			name: c.name,
			display: c.displayName || c.name,
			description: c.description || "",
		}))
	})
	const { records: masterRecords, size: masterRecordSize } = useSelector((state: RootState) =>
		displayRecords(state, tableName),
	)

	// 参照先のデータも取得しておく
	const referenceConfigs = useReferenceConfigs(tableName)
	React.useEffect(() => {
		for (const columnName of Object.keys(referenceConfigs)) {
			const { tableName: refTableName } = referenceConfigs[columnName]
			gqlClient()
				.masterRecords({ tableName: refTableName })
				.then((res) => {
					dispatch(
						masterRecordsSlice.actions.setData({
							tableName: refTableName,
							data: res.masterRecords,
							columnTypes: allColumnTypes[refTableName],
							additional: false,
						}),
					)
				})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableName])

	// local state

	const [settingColumn, setSettingColumn] = React.useState<string | null>(null)

	const [hasDuplicatedPrimaryKeys, setHasDuplicatePrimaryKeys] = React.useState<boolean>(false)

	const [deleteTableModalOpen, setDeleteTableModalOpen] = React.useState<boolean>(false)

	const [deleteTableModalButtonLoading, setDeleteTableModalButtonLoading] = React.useState<boolean>(false)

	const [isFinalizingTemporaryAlert, setIsFinalizingTemporaryAlert] = React.useState<boolean>(false)

	// hook

	const { startResize, finishResize, onResize, tableProps, tableRef } = useResizer(
		tableName,
		columnNames,
		tableLeftSpace,
	)

	// event handler

	const onClickDeleteRecord = React.useCallback(
		(recordId: string) => {
			gqlClient()
				.deleteMasterRecord({ recordId })
				.then(({ deleteMasterRecord: { result } }) => {
					if (result) {
						dispatch(masterRecordsSlice.actions.deleteRecords({ tableName, recordIds: [recordId] }))
					}
				})
		},
		[dispatch, tableName],
	)

	const onClickDeleteTableData = React.useCallback(() => {
		setDeleteTableModalOpen(true)
	}, [])

	const onClickConfirmationDeleteTableData = React.useCallback(
		(confirmationResult: boolean) => {
			if (confirmationResult) {
				// テーブルの削除操作がどのタイミングで行われているかどうかを確認するために、
				// テーブル名をSentryに送信する。
				// https://www.notion.so/skydisc/1c5d42d865fb41b2b841bfc0ac6c0bc6?pvs=4
				Sentry.captureMessage(`テーブルの削除操作が行われました: ${tableName}`, {
					level: "info",
				})

				setDeleteTableModalButtonLoading(true)
				gqlClient()
					.deleteMasterTableData({ tableName })
					.then(({ deleteMasterTableData: { result } }) => {
						setDeleteTableModalOpen(false)
						setDeleteTableModalButtonLoading(false)
						if (result) {
							dispatch(masterRecordsSlice.actions.deleteAllTableRows({ tableName }))
						} else {
							console.warn("deleteMasterTableData が失敗した")
						}
					})
			} else if (!deleteTableModalButtonLoading) {
				setDeleteTableModalOpen(false)
			} else {
				/** 現在、削除中なのでモーダルを閉じない */
			}
		},
		[dispatch, tableName, deleteTableModalButtonLoading],
	)

	const onClickCreateRecord = React.useCallback(() => {
		gqlClient()
			.createMasterRecord({ tableName })
			.then(({ createMasterRecord: { masterRecord: record } }) => {
				dispatch(masterRecordsSlice.actions.setData({ tableName, data: [record], columnTypes, additional: true }))
				dispatch(masterRecordsSlice.actions.resetFunction({ tableName }))
			})
	}, [columnTypes, dispatch, tableName])

	const onClickFinalizeRecord = React.useCallback(
		(recordId: string) => {
			gqlClient()
				.finalizeMasterRecord({ recordId })
				.then(({ finalizeMasterRecord: { result, errors } }) => {
					if (result) {
						dispatch(
							masterRecordsSlice.actions.setStage({ tableName, recordIds: [recordId], stage: MasterRecordStage.Final }),
						)
					} else if (errors) {
						dispatch(masterRecordsSlice.actions.setErrors(errors))
					}
				})
		},
		[dispatch, tableName],
	)

	const onClickUpload = React.useCallback(() => {
		dispatch(uiSlice.actions.setOpenFilePicker(true))
	}, [dispatch])

	const onClickDownload = React.useCallback(() => {
		gqlClient()
			.masterTableCsv({ tableName })
			.then(({ masterTableCsv: { filename, data } }) => {
				if (data) {
					download(document, base64ToBlob(data, "text/csv"), filename)
				}
			})
	}, [tableName])

	/**
	 * `stage:temporary`の物を`stage:final`にする
	 */
	const finalize = React.useCallback(() => {
		setIsFinalizingTemporaryAlert(true)
		gqlClient()
			.finalizeMasterTable({ tableName })
			.then(async ({ finalizeMasterTable: { recordIds, errors } }) => {
				if (recordIds) {
					const res = await gqlClient().masterRecords({ tableName })
					setIsFinalizingTemporaryAlert(false)

					dispatch(
						masterRecordsSlice.actions.setData({
							tableName,
							data: res.masterRecords,
							columnTypes,
							additional: false,
						}),
					)
				} else if (errors) {
					dispatch(masterRecordsSlice.actions.setErrors(errors))
				}
			})
	}, [dispatch, tableName])

	/** 重複しているレコードがあるかどうかをチェックをして、存在した場合Confirmを出す */
	const findDuplicateRecord = React.useCallback(() => {
		if (primaryKeyName === undefined) return

		const primaryKeys = tableData.map((record) => record.columns[primaryKeyName])
		const notDuplicate = new Set(primaryKeys)
		if (notDuplicate.size < primaryKeys.length) {
			setHasDuplicatePrimaryKeys(true)
		} else {
			finalize()
		}
	}, [finalize, primaryKeyName, tableData])

	/** 重複しているレコードがある場合に表示したConfirmを閉じる */
	const onClickCloseDuplicateAlert = React.useCallback(
		(isUpdate: boolean) => {
			if (isUpdate) {
				finalize()
			}
			setHasDuplicatePrimaryKeys(false)
		},
		[finalize],
	)

	/** tableNameが変更された場合、filterColumnsを初期化する */
	React.useEffect(() => {
		dispatch(masterRecordsSlice.actions.resetFilter({ tableName }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableName])

	return (
		<>
			<TemporaryAlert
				tableName={tableName}
				onClickFinalize={findDuplicateRecord}
				isFinalizing={isFinalizingTemporaryAlert}
			/>
			<Box onMouseMove={onResize} onMouseUp={finishResize}>
				<Stack
					direction="row"
					spacing={2}
					my={1}
					mx={`${tableLeftSpace}px`}
					justifyContent="flex-start"
					alignItems="top"
				>
					<Stack height={61} alignItems="middle" direction="row" spacing={2} my={1}>
						<FuncButton label="新しい行を追加します" icon={<BiPlusCircle />} onClick={onClickCreateRecord} />
						<FuncButton label="CSVファイルから行を追加します" icon={<BiUpload />} onClick={() => onClickUpload()} />
						<FuncButton
							label="CSVファイルをダウンロードします"
							icon={<BiDownload />}
							onClick={() => onClickDownload()}
						/>
						{tableSchema?.allowSmDelete /** このボタンは domain_definition の `allow_sm_delete` が true の場合のみ */ && (
							<FuncButton
								label="テーブルのデータを全て削除します"
								icon={<BiTrash />}
								color="secondary"
								onClick={() => onClickDeleteTableData()}
							/>
						)}
					</Stack>
					<TablePicker />
					{isFetchingMasterData ? (
						<CircularProgress size={24} style={{ marginLeft: "0.5em", marginTop: "1.5em" }} />
					) : (
						<></>
					)}
					<Box sx={{ flexGrow: 1 }}>
						<ColumnSearch tableName={tableName} />
						<TablePagination count={masterRecordSize} tableName={tableName} />
					</Box>
				</Stack>
				{/* Box 使うと MUI が tableProps をキャッシュしてしまうので div を使う */}
				<div style={{ ...tableProps, marginBottom: "120px" }} ref={tableRef} role="table" data-testid="table">
					<div />
					<ColumnFunction tableName={tableName} />
					{/* ヘッダー */}
					<Box borderRight={1} borderColor={headerBorderColor} />
					<Box role="row" aria-label="header" display="contents">
						{columnNames.map((columnName, i) => {
							return (
								<React.Fragment key={`header_${columnName.name}_${i}`}>
									<Box
										role="cell"
										position="relative"
										border={1}
										borderColor={headerBorderColor}
										borderLeft={0}
										aria-label={columnName.display}
									>
										<DnDHeader
											headerIndex={i}
											tableName={props.tableName}
											defaultColor={headerTextColor}
											dropActiveColor={headerTextColor2}
										>
											<Tooltip title={columnName.description} placement="top-start" enterDelay={1000} leaveDelay={100}>
												<Typography noWrap my={1} mx={2} fontWeight="bold" mr={4}>
													{columnName.display}
												</Typography>
											</Tooltip>
										</DnDHeader>
										<ColumnConfigButton onClick={() => setSettingColumn(columnName.name)} />
										<Resizer onMouseDown={(ev: React.MouseEvent) => startResize(ev, i)} />
									</Box>
								</React.Fragment>
							)
						})}
					</Box>

					{/* ボディー */}
					{masterRecords.map((record) => {
						return (
							<Box key={`record_${record.id}`} display="contents" role="row" aria-label="body">
								<Box borderRight={1} borderColor={cellBorderColor} width={`${tableLeftSpace}px`} px={1}>
									<Stack spacing={0} direction="row">
										<FuncMiniButton
											label="行を削除します"
											icon={<BiTrash />}
											onClick={() => onClickDeleteRecord(record.id)}
											hoverColor="red"
										/>
										{record.stage == "temporary" && (
											<FuncMiniButton
												label="行を確定します"
												icon={<BiCheckCircle />}
												onClick={() => onClickFinalizeRecord(record.id)}
												hoverColor="green"
											/>
										)}
									</Stack>
								</Box>

								{columnNames.map((columnName) => {
									return (
										<Box
											key={`${record.id}_${columnName.name}`}
											role="cell"
											data-testid={`cell_${record.id}_${columnName.name}`}
											border={1}
											borderColor={cellBorderColor}
											borderTop={0}
											borderLeft={0}
											bgcolor={record.stage == "temporary" ? temporaryCellColor : "white"}
										>
											<Cell
												recordId={record.id}
												columnName={columnName.name}
												tableName={tableName}
												data={record.columns[columnName.name]}
												displayData={record.displayColumns[columnName.name]}
											/>
										</Box>
									)
								})}
							</Box>
						)
					})}
				</div>
			</Box>

			<ColumnSettingModal
				isOpen={settingColumn != null}
				onClose={() => setSettingColumn(null)}
				columnName={settingColumn!}
				tableName={props.tableName}
			/>

			<DuplicatedPrimaryKeysConfirmationModal
				isOpen={hasDuplicatedPrimaryKeys}
				onClose={(event) => onClickCloseDuplicateAlert(event)}
				primaryKeyName={primaryKeyName ?? ""}
			/>

			<DeleteTableDataConfirmationModal
				isOpen={deleteTableModalOpen}
				onClose={(confirmationResult) => onClickConfirmationDeleteTableData(confirmationResult)}
				isButtonLoading={deleteTableModalButtonLoading}
			/>
		</>
	)
}
